import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WizardShell, { WizardStatus } from '../../components/WizardShell';
import Callout from '../../components/Callout';
import {
  CONSTRUCTION_SYSTEMS,
  getVertical,
  type ConstructionSystem,
} from '../../data/verticals';
import { useProject } from '../../lib/useProject';
import { cx } from '../../lib/cx';

const ALL_SYSTEMS = Object.keys(CONSTRUCTION_SYSTEMS) as ConstructionSystem[];

/** Four dots rather than a number, because "3 of 4" invites false precision. */
function Scale({ label, value }: { label: string; value: number }) {
  return (
    <span className="flex items-center gap-2">
      <span className="t-meta text-grey-600">{label}</span>
      <span className="flex gap-1" role="img" aria-label={`${label}: ${value} of 4`}>
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={cx(
              'h-2 w-2 rounded-full',
              i <= value ? 'bg-green-600' : 'bg-grey-200',
            )}
          />
        ))}
      </span>
    </span>
  );
}

function SystemCard({
  slug,
  suggested,
  selected,
  onSelect,
}: {
  slug: ConstructionSystem;
  suggested: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const s = CONSTRUCTION_SYSTEMS[slug];
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cx(
        'flex h-full w-full flex-col rounded-lg border p-6 text-left transition-colors',
        selected
          ? 'border-green-600 bg-green-050 shadow-[inset_0_0_0_2px_var(--color-green-600)]'
          : 'border-grey-200 bg-white shadow-sm hover:border-green-600',
      )}
    >
      <span className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="t-h3 text-ink">{s.label}</span>
        {suggested && (
          <span className="t-meta rounded-full bg-green-050 px-3 py-1 font-semibold text-green-900">
            Common for this
          </span>
        )}
      </span>
      <span className="t-body measure mt-3 flex-1 text-grey-600">{s.blurb}</span>
      <span className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
        <Scale label="Cost" value={s.relativeCost} />
        <Scale label="Speed" value={s.speed} />
      </span>
    </button>
  );
}

export default function System() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { project, loading, loadError, saveState, setField, flush } = useProject(id);
  const [busy, setBusy] = useState(false);

  const vertical = project?.verticalId ? getVertical(project.verticalId) : undefined;

  if (loading) return <WizardStatus message="Loading your project…" />;
  if (loadError) return <WizardStatus message={loadError} />;
  if (!project || !vertical) {
    return <WizardStatus message="We could not find that project." />;
  }

  const suggested = vertical.commonSystems;
  const rest = ALL_SYSTEMS.filter((s) => !suggested.includes(s));
  const chosen = project.constructionSystem as ConstructionSystem | null;

  const onContinue = async () => {
    setBusy(true);
    setField('status', 'styles');
    await flush();
    setBusy(false);
    navigate(`/project/${project.id}/styles`);
  };

  return (
    <WizardShell
      step={3}
      verticalLabel={vertical.label}
      title="How should it be built?"
      intro="This changes what the building can look like, what it costs, and how fast it goes up. Pick the one that sounds closest — you are not locked in."
      saveState={saveState}
      onBack={() => navigate(`/project/${project.id}/size`)}
      onContinue={() => void onContinue()}
      continueLabel="Pick the styles I like"
      blockedReason={chosen ? undefined : 'Choose a construction system to continue.'}
      busy={busy}
    >
      <h2 className="t-h2 text-ink">Commonly used for {vertical.label.toLowerCase()}</h2>
      <p className="t-meta measure mt-2 text-grey-600">
        These are suggestions, not restrictions. Every system below the line is still
        available, and plenty of good buildings get built the less obvious way.
      </p>

      <ul className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {suggested.map((s) => (
          <li key={s}>
            <SystemCard
              slug={s}
              suggested
              selected={chosen === s}
              onSelect={() => setField('constructionSystem', s)}
            />
          </li>
        ))}
      </ul>

      <h2 className="t-h2 mt-14 border-t border-grey-200 pt-10 text-ink">
        Everything else
      </h2>
      <ul className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {rest.map((s) => (
          <li key={s}>
            <SystemCard
              slug={s}
              suggested={false}
              selected={chosen === s}
              onSelect={() => setField('constructionSystem', s)}
            />
          </li>
        ))}
      </ul>

      <div className="mt-10">
        <Callout title="Not sure?">
          Pick whichever sounds closest. The construction system shapes the concept images
          we generate, and you can come back and change it to see the difference.
        </Callout>
      </div>
    </WizardShell>
  );
}
